/**
 * Сервисы Aptile — «приложения в приложении»: чат, звонок, справочник,
 * слова с Тико, «Снежный ком» и карман — плитками с крупной иконкой на
 * стекле и мягким бликом. Плитки сгруппированы по смыслу и подписаны:
 * общение, база знаний, игры. Нейросети здесь нет: до неё одно касание —
 * вкладка «Учиться».
 *
 * Цвета — только из палитры приложения (`theme/tokens`): тёплый песок,
 * мягкая бирюза и глубокий коричневый, вперемешку — сетка не делится на
 * одноцветные полосы. Текст на светлых плитках — коричневый, на тёмных —
 * песочный: контраст не ниже AA.
 */

import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { primeOnTap } from '../../services/micPermission';

import { IconBook, IconCards, IconLink, IconMessageCircle, IconPhone, IconSnowflake, type AppIcon } from '../icons';

type ServiceKey = 'chat' | 'call' | 'guide' | 'wordGame' | 'snowball' | 'pocket';

/** Тон плитки — из палитры приложения. */
export type ServiceTone = 'sand' | 'teal' | 'cocoa';

export const SERVICE_TONES: Readonly<Record<ServiceTone, { readonly gradient: string; readonly text: string; readonly muted: string; readonly glass: string }>> = {
  // accent → accentHover: тёплый песок, текст — heading (#4A3B31).
  sand: { gradient: 'linear-gradient(140deg, #F1D3A6 0%, #E8B87A 55%, #D99A5C 100%)', text: 'text-[#4A3B31]', muted: 'text-[#4A3B31]/75', glass: 'bg-white/35' },
  // teal → tealDeep: мягкая бирюза, текст — белый на глубокой бирюзе.
  teal: { gradient: 'linear-gradient(140deg, #8CC4C4 0%, #4F9293 50%, #2F6E6F 100%)', text: 'text-white', muted: 'text-white/85', glass: 'bg-white/20' },
  // heading: глубокий коричневый, текст — песочный.
  cocoa: { gradient: 'linear-gradient(140deg, #6E5646 0%, #4A3B31 60%, #3A2E26 100%)', text: 'text-[#F7F4ED]', muted: 'text-[#F7F4ED]/80', glass: 'bg-[#E8B87A]/25' },
};

interface Service {
  readonly key: ServiceKey;
  readonly to: string;
  readonly icon: AppIcon;
  readonly tone: ServiceTone;
}

type ServiceGroupKey = 'communication' | 'knowledge' | 'games';

interface ServiceGroup {
  readonly key: ServiceGroupKey;
  readonly services: readonly Service[];
}

export const SERVICE_GROUPS: readonly ServiceGroup[] = [
  {
    key: 'communication',
    services: [
      { key: 'chat', to: '/chat', icon: IconMessageCircle, tone: 'teal' },
      { key: 'call', to: '/call', icon: IconPhone, tone: 'sand' },
    ],
  },
  {
    key: 'knowledge',
    services: [
      { key: 'guide', to: '/guide', icon: IconBook, tone: 'cocoa' },
      { key: 'pocket', to: '/pocket', icon: IconCards, tone: 'cocoa' },
    ],
  },
  {
    key: 'games',
    services: [
      { key: 'wordGame', to: '/word-game', icon: IconLink, tone: 'sand' },
      { key: 'snowball', to: '/snowball', icon: IconSnowflake, tone: 'teal' },
    ],
  },
];

export const SERVICES: readonly Service[] = SERVICE_GROUPS.flatMap((group) => group.services);

const PRESS = { type: 'spring', stiffness: 500, damping: 24 } as const;

function Tile({ service, index }: { readonly service: Service; readonly index: number }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tone = SERVICE_TONES[service.tone];
  return (
    <motion.button
      type="button"
      onClick={() => {
        // Звонок начнётся без касаний — микрофон открываем сейчас, в жесте.
        if (service.to === '/call') primeOnTap();
        navigate(service.to);
      }}
      initial={{ opacity: 0, y: 14, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ ...PRESS, delay: index * 0.05 }}
      whileHover={{ y: -5, scale: 1.03 }}
      whileTap={{ scale: 0.95 }}
      style={{ backgroundImage: tone.gradient }}
      className={['relative flex min-h-[10.5rem] flex-col justify-between overflow-hidden rounded-[1.75rem] p-4 text-left shadow-card-lg ring-1 ring-white/20 sm:p-5', tone.text].join(' ')}
    >
      {/* Блик и свечение — «стекло» поверх градиента. */}
      <span aria-hidden="true" className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/25 blur-2xl" />
      <span aria-hidden="true" className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-black/10 blur-2xl" />
      <span className={['relative grid h-14 w-14 place-items-center rounded-2xl shadow-[inset_0_1px_0_rgb(255_255_255/0.4)] backdrop-blur-md', tone.glass].join(' ')}>
        <service.icon size={30} stroke={1.9} aria-hidden="true" />
      </span>
      <span className="relative flex flex-col gap-0.5">
        <span className="font-heading text-lg font-extrabold leading-tight drop-shadow-sm">{t(`dashboard.actions.${service.key}`)}</span>
        <span className={['text-xs leading-snug sm:text-sm', tone.muted].join(' ')}>{t(`dashboard.actions.${service.key}Hint`)}</span>
      </span>
    </motion.button>
  );
}

export function ServicesGrid() {
  const { t } = useTranslation();
  return (
    <div aria-label={t('services.title')} className="grid gap-6 lg:grid-cols-3 lg:gap-5">
      {SERVICE_GROUPS.map((group, groupIndex) => (
        <section key={group.key} aria-labelledby={`services-group-${group.key}`} className="flex flex-col gap-2.5">
          <h2 id={`services-group-${group.key}`} className="px-1 font-heading text-xs font-extrabold uppercase tracking-wide text-muted">
            {t(`services.groups.${group.key}`)}
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {group.services.map((service, index) => (
              <Tile key={service.key} service={service} index={groupIndex * 2 + index} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
